import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, Col, InputNumber, Progress, Row, Select, Space, Table, Tag, Typography, message, Empty, Badge, Input, Drawer, Collapse, Switch, Checkbox, Tabs, Modal } from "antd";
import { PlayCircleOutlined, ReloadOutlined, SaveOutlined, StopOutlined, ExpandOutlined, SettingOutlined, PlusOutlined, LinkOutlined, PauseCircleOutlined, RedoOutlined, CheckCircleOutlined, DeleteOutlined, FilterOutlined, DownloadOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { getCourseInfo, getCourseFilter, clearCoursesInfo } from "../../appRedux/actions/courses";
import CourseSkillsTab from "../../components/Courses/Skills/CourseSkillsTab";
import Page from "../../components/Common/Page";
import PagesHeader from "../../components/Common/PagesHeader";
import axiosHttp from "../../appRedux/services/axiosCommon";
import AIGeneratorConfig from "../../components/Common/AIGeneratorConfig";

const META_TAGS_BULK_SYSTEM_PROMPT = `You are an expert SEO copywriter specializing in online education content.

Your task is to generate a meta title and meta description for an online course page.

Language rule:
- Always respond in English, regardless of the course's original language, title script, or source content language. Never output non-English text in any field.

Tone rules:
- Always write in second person ("you", "your", "build", "become")
- Lead with the specific skill, tool, or outcome — never with a generic opening verb
- Do NOT begin title, description, og:title, or og:description with "Learn", "Master", "Discover", or "Unlock". Open with the subject itself (e.g. "R Data Analysis:" or "Go Interview Prep —" or a specific skill/tool name).
- Never start the description with the course title
- No exclamation marks anywhere in any field
- No generic CTA phrases — including but not limited to "enroll now," "start your journey," "land your dream job" — unless that exact outcome is explicitly stated in the provided outcomes
- Write for a learner scanning search results, not for an academic audience

Grounding rule:
- Only reference skills, tools, outcomes, or details explicitly present in the provided course data. Do not invent instructor credentials, claims, or specifics not present in the input.

Length rules (guidelines):
- title: target 50 to 60 characters
- description: target 140 to 155 characters
- og:title: target 50 to 60 characters
- og:description: target 140 to 155 characters, write shorter, not longer.

Keyword strategy:
- title: target the broader category keyword + core skill. The course title is context, not the keyword.
- description: lead with the primary keyword naturally in the first clause. Weave in 1–2 skills from the provided skills list.
- Do not stuff keywords — one primary, one or two secondary, woven naturally into readable copy.

Differentiation rules (critical — this is the primary reason this pipeline exists):
- The skills list is your primary differentiator. Two courses in the same category must produce visibly different copy if their skills differ.
- og:title and og:description must take a DIFFERENT ANGLE from title and description — not a reordering, not a synonym swap, not a verbatim repeat. Emphasize a different skill, outcome, or benefit than the meta fields do.
- No sequence of 4 or more consecutive words may appear in more than one of the four output fields (title, description, og:title, og:description).
- Never produce generic copy that could apply to any course in the category. Every output must be specific enough that removing the skills would make it meaningless.

Respond only with valid JSON. No markdown, no preamble, no explanation outside the JSON object.`;

const META_TAGS_BULK_USER_PROMPT = `Generate SEO meta tags for the following course.

---
Title: {{title}}
Subtitle: {{headline}}
Category: {{category}}
What you'll learn (outcomes): {{out_come}}
Skills: {{skills}}
Level: {{level}}
Who this course is for: {{level_summary}}
Description: {{description}}
---

Respond with this exact JSON structure:

{
  "meta": [
    { "name": "title", "content": "..." },
    { "name": "description", "content": "..." },
    { "name": "og:title", "content": "..." },
    { "name": "og:description", "content": "..." }
  ]
}

STRICT LENGTH LIMITS — DO NOT EXCEED, UNDER ANY CIRCUMSTANCE:
- title: minimum 50, maximum 60 characters
- description: minimum 140, maximum 155 characters
- og:title: minimum 50, maximum 60 characters
- og:description: minimum 140, maximum 155 characters
A field that exceeds its maximum by even one character is an invalid response. A title at 55 characters is correct; a title at 65 is a failure. A description at 150 is correct; a description at 190 is a failure.

Other rules:
- Respond in English only, regardless of the course's original language.
- og:title and og:description must take a different angle from title/description — not a reordering, not a repeat, and must not reuse any phrase of 4 or more consecutive words found in title or description.
- do not include any text outside the JSON object
- CRITICAL: Start your response directly with '{'. No conversational text before the JSON.`;

const META_TAGS_BULK_INTERNAL_INSTRUCTIONS = `Return only valid JSON with key: meta (array of name/content objects). No markdown, no preamble, no reasoning.`;

const CONTENT_PROCESSOR_TYPES = ["LevelBulk", "MetaTagsBulk", "FaqsBulk"];

const aiSettingsCategoryForProcessor = (processorType) => {
  if (processorType === "SkillsV2") return "Skills Bulk V2";
  if (processorType === "LevelBulk") return "Bulk Level";
  if (processorType === "MetaTagsBulk") return "Bulk Meta Tags";
  if (processorType === "FaqsBulk") return "Bulk FAQs";
  return "";
};

const statusColor = {
  queued: "blue",
  running: "processing",
  completed: "green",
  failed: "red",
  cancelled: "warning",
  pending: "default",
  generating: "processing",
  needs_review: "gold",
  saved: "green",
  skipped: "default",
};

const { Text } = Typography;

const percentFromTotals = (totals = {}) => {
  const total = totals.total || 0;
  if (!total) return 0;
  const done = (totals.needsReview || 0) + (totals.saved || 0) + (totals.failed || 0) + (totals.skipped || 0);
  return Math.round((done / total) * 100);
};

const normalizeSkillLookup = (value = "") =>
  String(value)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const findSkillOptionByName = (skillOptions = [], name = "") => {
  const target = normalizeSkillLookup(name);
  if (!target) return null;
  return skillOptions.find((option) =>
    [option.label, option.code, option.slug].some((value) => normalizeSkillLookup(value) === target)
  ) || null;
};

const decisionNameMatches = (decision = {}, name = "") => {
  const target = normalizeSkillLookup(name);
  if (!target) return false;
  return [decision.generated, decision.name].some((value) => normalizeSkillLookup(value) === target);
};

const V2MissingSkillsReview = ({ item, jobId, missing, skillOptions, onSaved }) => {
  const [gaps, setGaps] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const savedNewSkills = item.reviewData?.decisions?.newSkills || [];
    const rejectedSkills = item.reviewData?.decisions?.rejected || [];
    setGaps((missing || []).map((entry, index) => {
      const originalName = entry.name || "";
      const savedDecision = savedNewSkills.find((decision) => decisionNameMatches(decision, originalName));
      const rejectedDecision = rejectedSkills.find((decision) => decisionNameMatches(decision, originalName));
      return {
        key: `${originalName || "gap"}-${index}`,
        originalName,
        name: savedDecision?.name || originalName,
        parentId: savedDecision?.parentId || findSkillOptionByName(skillOptions, entry.suggestedParent)?.value || null,
        suggestedParent: entry.suggestedParent || "",
        reasoning: entry.reasoning || "",
        decision: savedDecision ? "saved" : rejectedDecision ? "discarded" : "pending",
      };
    }));
  }, [item._id, item.reviewData, missing, skillOptions]);

  const updateGap = (index, field, value) => {
    setGaps((prev) => prev.map((gap, idx) => (idx === index ? { ...gap, [field]: value } : gap)));
  };

  const handleCommit = async () => {
    const newSkills = gaps
      .filter((gap) => ["save", "saved"].includes(gap.decision))
      .map((gap) => ({ generated: gap.originalName, name: gap.name, parentId: gap.parentId || null }))
      .filter((gap) => gap.name && gap.name.trim());
    const rejected = gaps
      .filter((gap) => ["discard", "discarded"].includes(gap.decision))
      .map((gap) => ({ generated: gap.originalName || gap.name }));

    if (newSkills.length === 0 && rejected.length === 0) {
      message.info("Select at least one taxonomy gap to add or discard.");
      return;
    }

    setSaving(true);
    try {
      await axiosHttp.post(`/v1/ai-bulk-jobs/${jobId}/items/${item._id}/save-skills`, {
        accepted: [],
        newSkills,
        rejected,
      });
      message.success("Taxonomy gaps committed.");
      onSaved();
    } catch (error) {
      console.error("SkillsV2 taxonomy gap save error:", error);
      message.error(error.response?.data?.message || "Failed to commit taxonomy gaps.");
    } finally {
      setSaving(false);
    }
  };

  if (!gaps.length) return <Text type="secondary">None</Text>;

  const hasDecisions = gaps.some((gap) => gap.decision === "save" || gap.decision === "discard");

  return (
    <Space direction="vertical" size={8} style={{ width: "100%", marginTop: 8 }}>
      {gaps.map((gap, index) => (
        <div
          key={gap.key}
          className="border rounded"
          style={{
            padding: 8,
          }}
        >
          <Row gutter={[10, 10]} align="top">
            <Col xs={24} md={10} lg={8}>
              <Input
                value={gap.name}
                onChange={(event) => updateGap(index, "name", event.target.value)}
                placeholder="Skill name"
                style={{ width: "100%" }}
                disabled={["discard", "saved", "discarded"].includes(gap.decision)}
              />
            </Col>
            <Col xs={24} md={10} lg={8}>
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                value={gap.parentId}
                onChange={(value) => updateGap(index, "parentId", value)}
                options={skillOptions}
                placeholder="Parent skill"
                style={{ width: "100%" }}
                disabled={["discard", "saved", "discarded"].includes(gap.decision)}
              />
            </Col>
            <Col xs={24} md={4} lg={8}>
              <Space wrap>
                <Button
                  size="small"
                  icon={<PlusOutlined />}
                  type={gap.decision === "save" ? "primary" : "default"}
                  disabled={["saved", "discarded"].includes(gap.decision)}
                  onClick={() => updateGap(index, "decision", "save")}
                >
                  Add Skill
                </Button>
                <Button
                  size="small"
                  danger
                  type={gap.decision === "discard" ? "primary" : "text"}
                  disabled={["saved", "discarded"].includes(gap.decision)}
                  onClick={() => updateGap(index, "decision", "discard")}
                >
                  Discard
                </Button>
              </Space>
            </Col>
          </Row>
          <div style={{ minWidth: 0, marginTop: 8 }}>
            {gap.suggestedParent && <Tag color="geekblue">Suggested parent: {gap.suggestedParent}</Tag>}
            {gap.reasoning && <Text type="secondary" style={{ display: "block", wordBreak: "break-word", whiteSpace: "normal" }}>{gap.reasoning}</Text>}
            {gap.decision === "saved" && <Tag color="green">Committed</Tag>}
            {gap.decision === "discarded" && <Tag>Discarded</Tag>}
          </div>
        </div>
      ))}
      <div className="d-flex justify-content-end">
        <Button type="primary" icon={<SaveOutlined />} loading={saving} disabled={!hasDecisions} onClick={handleCommit}>
          Commit Taxonomy Gaps
        </Button>
      </div>
    </Space>
  );
};

const BulkSkillsReviewItem = ({ item, skillOptions, onSaved }) => {
  const [matchedSkills, setMatchedSkills] = useState([]);
  const [newSkills, setNewSkills] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setMatchedSkills((item.reviewData?.matched || []).map((match) => ({ ...match, decision: "pending" })));
    setNewSkills((item.reviewData?.new || []).map((entry) => ({
      ...entry,
      name: entry.name || entry.generated,
      parentId: entry.parentId || null,
      decision: "pending",
    })));
  }, [item._id]);

  const updateMatchedDecision = (index, decision) => {
    const nextMatched = [...matchedSkills];
    const selected = nextMatched[index];

    if (decision === "save_as_new") {
      nextMatched.splice(index, 1);
      setNewSkills([...newSkills, { generated: selected.generated, name: selected.generated, parentId: null, decision: "pending" }]);
    } else if (decision === "create_child") {
      nextMatched.splice(index, 1);
      setNewSkills([
        ...newSkills,
        {
          generated: selected.generated,
          name: selected.generated,
          parentId: selected.matchedSkill?._id || null,
          suggestedParentName: selected.matchedSkill?.name || "",
          decision: "pending",
        },
      ]);
    } else {
      nextMatched[index] = { ...selected, decision };
    }

    setMatchedSkills(nextMatched);
  };

  const updateNewSkill = (index, field, value) => {
    const next = [...newSkills];
    next[index] = { ...next[index], [field]: value };
    setNewSkills(next);
  };

  const handleSave = async () => {
    const accepted = matchedSkills
      .filter((match) => match.decision === "accept")
      .map((match) => ({ skillId: match.matchedSkill?._id }))
      .filter((entry) => entry.skillId);

    const newSkillsPayload = newSkills
      .filter((entry) => entry.decision === "save")
      .map((entry) => ({ name: entry.name, parentId: entry.parentId || null }))
      .filter((entry) => entry.name);

    const rejected = [
      ...matchedSkills.filter((match) => match.decision === "discard").map((match) => ({ generated: match.generated })),
      ...newSkills.filter((entry) => entry.decision === "discard").map((entry) => ({ generated: entry.generated })),
    ];

    if (accepted.length === 0 && newSkillsPayload.length === 0 && rejected.length === 0) {
      message.info("No review decisions selected for this course.");
      return;
    }

    setSaving(true);
    try {
      await axiosHttp.post(`/v1/ai-bulk-jobs/${item.jobId}/items/${item._id}/save-skills`, {
        accepted,
        newSkills: newSkillsPayload,
        rejected,
      });
      message.success("Skill decisions saved.");
      onSaved();
    } catch (error) {
      console.error("Bulk skill save error:", error);
      message.error(error.response?.data?.message || "Failed to save skill decisions.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      size="small"
      title={<span>{item.courseTitle || item.courseId}</span>}
      extra={<Tag color={statusColor[item.status]}>{item.status}</Tag>}
      style={{ marginBottom: 12, borderRadius: 6 }}
    >
      {matchedSkills.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <Text strong>Matched Suggestions</Text>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
            {matchedSkills.map((match, index) => (
              <div key={`${match.generated}-${index}`} className="d-flex align-items-center justify-content-between p-2 border rounded">
                <div>
                  <Text strong>{match.generated}</Text>
                  <div style={{ fontSize: 12, color: "#6b7280", marginTop: 3 }}>
                    Suggested: <Tag color="green">{match.matchedSkill?.name || "Unknown"}</Tag>
                    <Badge count={`${Math.round((match.confidence || 0) * 100)}%`} style={{ backgroundColor: "#52c41a" }} />
                  </div>
                </div>
                <Space wrap>
                  <Button size="small" type={match.decision === "accept" ? "primary" : "default"} onClick={() => updateMatchedDecision(index, "accept")}>
                    Accept Match
                  </Button>
                  <Button size="small" onClick={() => updateMatchedDecision(index, "create_child")} disabled={!match.matchedSkill?._id}>
                    Create as Child
                  </Button>
                  <Button size="small" onClick={() => updateMatchedDecision(index, "save_as_new")}>
                    Save as New
                  </Button>
                  <Button size="small" danger type={match.decision === "discard" ? "primary" : "text"} onClick={() => updateMatchedDecision(index, "discard")}>
                    Discard
                  </Button>
                </Space>
              </div>
            ))}
          </div>
        </div>
      )}

      {newSkills.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <Text strong>New Skills</Text>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
            {newSkills.map((entry, index) => (
              <div key={`${entry.generated}-${index}`} className="d-flex align-items-center justify-content-between p-2 border rounded">
                <Space wrap style={{ flex: 1 }}>
                  <Input
                    value={entry.name}
                    onChange={(event) => updateNewSkill(index, "name", event.target.value)}
                    placeholder="Skill name"
                    style={{ width: 220 }}
                  />
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    value={entry.parentId}
                    onChange={(value) => updateNewSkill(index, "parentId", value)}
                    options={skillOptions}
                    placeholder="Parent skill"
                    style={{ width: 260 }}
                  />
                  {entry.suggestedParentName && <Tag color="geekblue">Child of {entry.suggestedParentName}</Tag>}
                </Space>
                <Space>
                  <Button size="small" type={entry.decision === "save" ? "primary" : "default"} onClick={() => updateNewSkill(index, "decision", "save")}>
                    Save
                  </Button>
                  <Button size="small" danger type={entry.decision === "discard" ? "primary" : "text"} onClick={() => updateNewSkill(index, "decision", "discard")}>
                    Discard
                  </Button>
                </Space>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="d-flex justify-content-between align-items-center">
        <Text type="secondary">
          Tokens: {item.usage?.totalTokens || 0}
          {item.error ? ` | Error: ${item.error}` : ""}
        </Text>
        <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
          Save Decisions
        </Button>
      </div>
    </Card>
  );
};

const AIBulkJobs = () => {
  const dispatch = useDispatch();
  const { courseFilters, courseInfo } = useSelector((state) => state.courses);
  const [manualModalVisible, setManualModalVisible] = useState(false);
  const [manualCourseId, setManualCourseId] = useState(null);
  const [manualCourseTitle, setManualCourseTitle] = useState("");

  const handleOpenManualModal = (courseId, courseTitle) => {
    if (!courseId) {
      message.error("Course ID not found.");
      return;
    }
    dispatch(clearCoursesInfo());
    setManualCourseId(courseId);
    setManualCourseTitle(courseTitle || "Course");
    
    // Only fetch filter options if they are not already loaded
    if (!courseFilters || !courseFilters.skills || courseFilters.skills.length === 0) {
      dispatch(getCourseFilter({}));
    }
    setManualModalVisible(true);
  };

  const handleCloseManualModal = (updatedSkills) => {
    let skillsToUse = null;
    if (Array.isArray(updatedSkills)) {
      skillsToUse = updatedSkills;
    } else if (manualCourseId && courseInfo && Array.isArray(courseInfo.skills) && courseInfo.skills.length > 0) {
      skillsToUse = courseInfo.skills;
    }

    // Optimistically patch the local jobDetail with skills from the just-saved skills list
    if (manualCourseId && skillsToUse) {
      const savedSkillTags = skillsToUse.map((skill) => {
        if (typeof skill === "string") {
          // Look up name from courseFilters
          const found = courseFilters?.skills?.find((s) => s._id === skill);
          return { skillId: skill, skillName: found?.name || found?.code || skill };
        }
        return { skillId: skill._id, skillName: skill.name || skill.code || skill._id };
      });

      setJobDetail((prev) => {
        if (!prev || !prev.items) return prev;
        return {
          ...prev,
          items: prev.items.map((item) =>
            item.courseId === manualCourseId
              ? { ...item, decisions: { ...item.decisions, autoLinked: savedSkillTags } }
              : item
          ),
        };
      });
    }

    setManualModalVisible(false);
    setManualCourseId(null);
    setManualCourseTitle("");
    dispatch(clearCoursesInfo());
  };

  const [categories, setCategories] = useState([]);
  const [skillOptions, setSkillOptions] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [jobDetail, setJobDetail] = useState(null);
  const [categoryId, setCategoryId] = useState(null);
  const [limit, setLimit] = useState(50);
  const [batchSize, setBatchSize] = useState(10);
  const [creating, setCreating] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [pipelineType, setPipelineType] = useState("SkillsV2");
  const [topK, setTopK] = useState(20);
  const [scoreFloor, setScoreFloor] = useState(0.72);
  const [maxAutoLinksPerCourse, setMaxAutoLinksPerCourse] = useState(8);
  const [skipIfSkillsExist, setSkipIfSkillsExist] = useState(false);
  const [bulkModel, setBulkModel] = useState("");
  const [availableModels, setAvailableModels] = useState([]);
  const [bulkTemperature, setBulkTemperature] = useState(0.35);
  const [bulkMaxTokens, setBulkMaxTokens] = useState(2048);
  const [bulkDryRun, setBulkDryRun] = useState(false);
  const [bulkReplaceFaqs, setBulkReplaceFaqs] = useState(false);
  const [bulkFaqCount, setBulkFaqCount] = useState(5);
  const [bulkSystemPrompt, setBulkSystemPrompt] = useState("");
  const [bulkUserPrompt, setBulkUserPrompt] = useState("");
  const [bulkInternalInstructions, setBulkInternalInstructions] = useState("");
  const [bulkContextFields, setBulkContextFields] = useState([]);
  const [loadingAiDefaults, setLoadingAiDefaults] = useState(false);
  const [isCourseMonitorOpen, setIsCourseMonitorOpen] = useState(false);
  const [courseMonitorSearch, setCourseMonitorSearch] = useState("");
  const [courseMonitorStatus, setCourseMonitorStatus] = useState("all");
  const [bulkV2ConfigVisible, setBulkV2ConfigVisible] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const getBulkApiBase = (type) => {
    if (type === "SkillsV2") return "/v2/ai-bulk-jobs";
    if (CONTENT_PROCESSOR_TYPES.includes(type)) return "/v3/ai-bulk-jobs";
    return "/v1/ai-bulk-jobs";
  };

  const fetchCloudflareModels = async () => {
    try {
      const response = await axiosHttp.get("/v2/ai/models", {
        params: { task: "Text Generation", hide_experimental: true, per_page: 100 }
      });
      if (response.data?.status && response.data?.results?.result) {
        const cloudflareModels = response.data.results.result.map(m => m.name);
        setAvailableModels(cloudflareModels);
      }
    } catch (e) {
      console.warn("Could not load Cloudflare models:", e);
    }
  };

  const loadAiDefaultsForProcessor = async () => {
    const cat = aiSettingsCategoryForProcessor(pipelineType);
    if (!cat) return;
    setLoadingAiDefaults(true);
    try {
      const response = await axiosHttp.get("/v2/admin/ai/config", { params: { category: cat } });
      if (response.data?.status && response.data.results) {
        const c = response.data.results;
        setBulkModel(c.model || "");
        if (c.temperature != null) setBulkTemperature(Number(c.temperature));
        if (c.maxTokens != null) setBulkMaxTokens(Number(c.maxTokens));
        if (c.topK != null) setTopK(Number(c.topK));
        if (c.scoreFloor != null) setScoreFloor(Number(c.scoreFloor));
        if (c.maxAutoLinksPerCourse != null) setMaxAutoLinksPerCourse(Number(c.maxAutoLinksPerCourse));
        setBulkSystemPrompt(c.systemPrompt || "");
        setBulkUserPrompt(c.userPrompt || "");
        setBulkInternalInstructions(c.internalInstructions || "");
        setBulkContextFields(Array.isArray(c.contextFields) && c.contextFields.length ? c.contextFields : []);
      }
    } catch (e) {
      console.warn("Could not load AI defaults:", e);
      message.warning("Could not load saved AI settings; using form values.");
    } finally {
      setLoadingAiDefaults(false);
    }
  };

  const loadCategories = async () => {
    const response = await axiosHttp.get("/v1/category");
    if (response.data?.status) {
      setCategories((response.data.results || []).map((category) => ({
        value: category._id,
        label: category.name || category.code,
      })));
    }
  };

  const loadSkills = async () => {
    try {
      const response = await axiosHttp.get("/v1/skill");
      if (response.data?.status) {
        setSkillOptions((response.data.results || []).map((skill) => ({
          value: skill._id,
          label: skill.name || skill.code,
          code: skill.code,
          slug: skill.slug,
        })));
      }
    } catch (error) {
      console.warn("Could not load skill options:", error);
    }
  };

  const loadJobs = async (type = pipelineType, forceSelectFirst = false) => {
    const base = getBulkApiBase(type);
    const url = CONTENT_PROCESSOR_TYPES.includes(type) ? `${base}?type=${encodeURIComponent(type)}` : base;
    const response = await axiosHttp.get(url);
    if (response.data?.status) {
      const nextJobs = response.data.results || [];
      setJobs(nextJobs);
      if (forceSelectFirst && nextJobs.length > 0) {
        setSelectedJobId(nextJobs[0]._id);
      }
    }
  };

  const resolveJobEndpointType = (jobId) => {
    const fromList = jobs.find((j) => j._id === jobId);
    if (fromList?.type) return fromList.type;
    if (jobDetail?.job?._id === jobId && jobDetail.job.type) return jobDetail.job.type;
    return pipelineType;
  };

  const loadJobDetail = async (jobId = selectedJobId) => {
    if (!jobId) return;
    setLoadingDetail(true);
    try {
      const endpointType = resolveJobEndpointType(jobId);
      const response = await axiosHttp.get(`${getBulkApiBase(endpointType)}/${jobId}`);
      if (response.data?.status) {
        setJobDetail(response.data.results);
      }
    } catch (error) {
      console.error("Load bulk job detail error:", error);
      message.error("Failed to load job details.");
    } finally {
      setLoadingDetail(false);
    }
  };

  useEffect(() => {
    loadCategories();
    loadSkills();
    loadJobs(pipelineType, false);
    fetchCloudflareModels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isCourseMonitorOpen) {
      document.body.classList.add("mini-sidebar");
    } else {
      document.body.classList.remove("mini-sidebar");
    }
    return () => {
      document.body.classList.remove("mini-sidebar");
    };
  }, [isCourseMonitorOpen]);

  useEffect(() => {
    if (pipelineType === "SkillsV2" || CONTENT_PROCESSOR_TYPES.includes(pipelineType)) {
      loadAiDefaultsForProcessor();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pipelineType]);

  useEffect(() => {
    if (selectedJobId) loadJobDetail(selectedJobId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedJobId]);

  useEffect(() => {
    loadJobs(pipelineType, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pipelineType]);

  useEffect(() => {
    const status = jobDetail?.job?.status;
    if (!selectedJobId || !["queued", "running"].includes(status)) return undefined;

    const timer = setInterval(() => {
      loadJobs();
      loadJobDetail(selectedJobId);
    }, 5000);

    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedJobId, jobDetail?.job?.status]);

  const createJob = async () => {
    if (!categoryId) {
      message.warning("Select a category first.");
      return;
    }

    setCreating(true);
    try {
      const payload = {
        categoryId,
        limit: limit ?? 50,
        batchSize: batchSize ?? 10,
        skipIfSkillsExist,
      };
      if (pipelineType === "SkillsV2") {
        payload.topK = topK ?? 20;
        payload.scoreFloor = scoreFloor ?? 0.72;
        payload.maxAutoLinksPerCourse = maxAutoLinksPerCourse ?? 8;
      }
      if (CONTENT_PROCESSOR_TYPES.includes(pipelineType)) {
        payload.type = pipelineType;
        if (bulkModel.trim()) payload.model = bulkModel.trim();
        payload.temperature = bulkTemperature ?? 0.35;
        payload.maxTokens = bulkMaxTokens ?? 2048;
        payload.dryRun = bulkDryRun;
        if (pipelineType === "FaqsBulk") {
          payload.replaceFaqs = bulkReplaceFaqs;
          payload.faqCount = bulkFaqCount ?? 5;
        }
        if (bulkSystemPrompt.trim()) payload.systemPrompt = bulkSystemPrompt.trim();
        if (bulkUserPrompt.trim()) payload.userPrompt = bulkUserPrompt.trim();
        if (bulkInternalInstructions.trim()) payload.internalInstructions = bulkInternalInstructions.trim();
        if (bulkContextFields.length) payload.contextFields = bulkContextFields;
      }

      const response = await axiosHttp.post(getBulkApiBase(pipelineType), payload);

      if (response.data?.status) {
        message.success("Bulk job created.");
        const job = response.data.results;
        setSelectedJobId(job._id);
        setIsCreateModalOpen(false);
        await loadJobs();
        await loadJobDetail(job._id);
      }
    } catch (error) {
      console.error("Create bulk job error:", error);
      message.error(error.response?.data?.message || "Failed to create bulk job.");
    } finally {
      setCreating(false);
    }
  };

  const runJob = async () => {
    if (!selectedJobId) return;
    const endpointType = resolveJobEndpointType(selectedJobId);
    const isPaused = jobDetail?.job?.status === "cancelled";
    await axiosHttp.post(`${getBulkApiBase(endpointType)}/${selectedJobId}/run`);
    message.success(isPaused ? "Job resumed." : "Job queued.");
    loadJobs();
    loadJobDetail(selectedJobId);
  };

  const pauseJob = async () => {
    if (!selectedJobId) return;
    const endpointType = resolveJobEndpointType(selectedJobId);
    await axiosHttp.post(`${getBulkApiBase(endpointType)}/${selectedJobId}/cancel`);
    message.success("Pausing job... (will pause after the current course finishes)");
    loadJobs();
    loadJobDetail(selectedJobId);
  };

  const handleExportJSON = () => {
    if (!jobDetail?.job) return;
    
    const exportData = {
      jobId: jobDetail.job._id,
      type: jobDetail.job.type,
      status: jobDetail.job.status,
      categoryName: jobDetail.job.filters?.categoryName,
      configSettings: jobDetail.job.configSnapshot,
      totals: jobDetail.job.totals,
      results: (jobDetail.items || []).map(item => ({
        courseId: item.courseId?._id || item.courseId,
        courseTitle: item.courseTitle || item.courseId?.title,
        status: item.status,
        error: item.error,
        decisions: item.decisions,
      }))
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `BulkJob_${jobDetail.job.type}_${jobDetail.job._id}_Export.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const retryFailed = async () => {
    if (!selectedJobId) return;
    setRetrying(true);
    try {
      const endpointType = resolveJobEndpointType(selectedJobId);
      await axiosHttp.post(`${getBulkApiBase(endpointType)}/${selectedJobId}/run`);
      message.success("Retrying failed courses...");
      await loadJobs();
      await loadJobDetail(selectedJobId);
    } catch (error) {
      console.error("Retry failed job error:", error);
      message.error(error.response?.data?.message || "Failed to retry job.");
    } finally {
      setRetrying(false);
    }
  };

  const needsReviewItems = useMemo(
    () => (jobDetail?.items || []).filter((item) => item.status === "needs_review"),
    [jobDetail]
  );

  // --- Skill Enrichment: aggregate all missing skills across the entire bulk run ---
  const aggregatedMissingSkills = useMemo(() => {
    if (!jobDetail?.items || jobDetail?.job?.type !== "SkillsV2") return [];
    const map = new Map(); // key = normalizedName, value = { name, suggestedParent, reasoning, sources: [{ itemId, courseTitle, courseId }] }
    for (const item of jobDetail.items) {
      const missing = item.decisions?.missing || [];
      const savedNew = item.reviewData?.decisions?.newSkills || [];
      const rejected = item.reviewData?.decisions?.rejected || [];
      for (const entry of missing) {
        const raw = entry.name || "";
        const key = normalizeSkillLookup(raw);
        if (!key) continue;
        // Check if this gap was already committed for this item
        const alreadySaved = savedNew.some((d) => decisionNameMatches(d, raw));
        const alreadyRejected = rejected.some((d) => decisionNameMatches(d, raw));
        // Check if the skill already exists in the taxonomy
        const existsInTaxonomy = skillOptions.some((opt) => normalizeSkillLookup(opt.label) === key);
        if (alreadySaved || alreadyRejected || existsInTaxonomy) continue;
        if (!map.has(key)) {
          map.set(key, {
            key,
            name: raw,
            suggestedParent: entry.suggestedParent || "",
            reasoning: entry.reasoning || "",
            parentId: findSkillOptionByName(skillOptions, entry.suggestedParent)?.value || null,
            sources: [],
          });
        }
        map.get(key).sources.push({
          itemId: item._id,
          courseTitle: item.courseTitle || item.courseId,
          courseId: item.courseId,
        });
      }
    }
    return Array.from(map.values());
  }, [jobDetail, skillOptions]);

  const [enrichmentDecisions, setEnrichmentDecisions] = useState({});
  const [enrichmentSaving, setEnrichmentSaving] = useState(false);

  // Reset enrichment decisions when job detail changes
  useEffect(() => {
    setEnrichmentDecisions({});
  }, [jobDetail?.job?._id]);

  const updateEnrichmentDecision = (key, field, value) => {
    setEnrichmentDecisions((prev) => ({
      ...prev,
      [key]: { ...prev[key], [field]: value },
    }));
  };

  const commitEnrichment = async () => {
    // Group decisions by item
    const itemPayloads = new Map(); // itemId -> { newSkills: [], rejected: [] }
    for (const skill of aggregatedMissingSkills) {
      const dec = enrichmentDecisions[skill.key];
      if (!dec?.decision) continue;
      const finalName = dec.name ?? skill.name;
      const finalParentId = dec.parentId ?? skill.parentId;
      for (const src of skill.sources) {
        if (!itemPayloads.has(src.itemId)) {
          itemPayloads.set(src.itemId, { newSkills: [], rejected: [] });
        }
        const payload = itemPayloads.get(src.itemId);
        if (dec.decision === "add") {
          payload.newSkills.push({ generated: skill.name, name: finalName, parentId: finalParentId || null });
        } else if (dec.decision === "discard") {
          payload.rejected.push({ generated: skill.name });
        }
      }
    }

    if (itemPayloads.size === 0) {
      message.info("No enrichment decisions to commit.");
      return;
    }

    setEnrichmentSaving(true);
    try {
      const promises = Array.from(itemPayloads.entries()).map(([itemId, payload]) =>
        axiosHttp.post(`/v1/ai-bulk-jobs/${selectedJobId}/items/${itemId}/save-skills`, {
          accepted: [],
          newSkills: payload.newSkills,
          rejected: payload.rejected,
        })
      );
      await Promise.all(promises);
      message.success(`Enrichment committed across ${itemPayloads.size} course(s).`);
      setEnrichmentDecisions({});
      loadSkills();
      loadJobs();
      loadJobDetail(selectedJobId);
    } catch (error) {
      console.error("Enrichment commit error:", error);
      message.error(error.response?.data?.message || "Failed to commit enrichment.");
    } finally {
      setEnrichmentSaving(false);
    }
  };

  const [enrichmentDeduping, setEnrichmentDeduping] = useState(false);

  const dedupEnrichment = async () => {
    const pendingSkills = aggregatedMissingSkills.filter((s) => {
      const dec = enrichmentDecisions[s.key];
      return !dec?.decision; // only dedup undecided skills
    });
    if (pendingSkills.length === 0) {
      message.info("No pending skills to deduplicate.");
      return;
    }

    setEnrichmentDeduping(true);
    try {
      message.loading({ content: "Running deduplication...", key: "enrichDedup" });
      const response = await axiosHttp.post("/v1/skills/deduplicate", {
        skills: pendingSkills.map((s) => enrichmentDecisions[s.key]?.name ?? s.name),
      });

      if (response.data?.status && response.data?.results) {
        const { matched = [], new: newUnmatched = [] } = response.data.results;
        const matchedLookupMap = new Map();
        matched.forEach((m) => {
          const genKey = normalizeSkillLookup(m.generated || m.name || "");
          const matchName = m.matchedSkill?.name || m.name || "";
          if (genKey) {
            matchedLookupMap.set(genKey, matchName);
          }
        });

        const updatedDecisions = { ...enrichmentDecisions };
        for (const skill of pendingSkills) {
          const skillName = updatedDecisions[skill.key]?.name ?? skill.name;
          const normalized = normalizeSkillLookup(skillName);
          if (matchedLookupMap.has(normalized)) {
            // Already exists in taxonomy → auto-discard
            updatedDecisions[skill.key] = {
              ...updatedDecisions[skill.key],
              decision: "discard",
              matchedSkillName: matchedLookupMap.get(normalized),
            };
          } else {
            // Truly new → auto-add
            updatedDecisions[skill.key] = {
              ...updatedDecisions[skill.key],
              decision: "add",
              matchedSkillName: undefined,
            };
          }
        }
        setEnrichmentDecisions(updatedDecisions);

        const discardedCount = pendingSkills.filter((s) => matchedLookupMap.has(normalizeSkillLookup(updatedDecisions[s.key]?.name ?? s.name))).length;
        const newCount = pendingSkills.length - discardedCount;
        message.success({ content: `Dedup complete: ${discardedCount} duplicates discarded, ${newCount} new skills to add.`, key: "enrichDedup" });
      }
    } catch (error) {
      console.error("Enrichment dedup error:", error);
      message.error({ content: "Failed to run deduplication.", key: "enrichDedup" });
    } finally {
      setEnrichmentDeduping(false);
    }
  };

  const [individualDeduping, setIndividualDeduping] = useState({});

  const dedupIndividualEnrichment = async (key, name) => {
    setIndividualDeduping((prev) => ({ ...prev, [key]: true }));
    try {
      const response = await axiosHttp.post("/v1/skills/deduplicate", {
        skills: [name],
      });
      if (response.data?.status && response.data?.results) {
        const { matched = [] } = response.data.results;
        const matchedObj = matched.find(
          (m) => normalizeSkillLookup(m.generated || m.name || "") === normalizeSkillLookup(name)
        );
        if (matchedObj) {
          const matchedName = matchedObj.matchedSkill?.name || matchedObj.name || "";
          setEnrichmentDecisions((prev) => ({
            ...prev,
            [key]: {
              ...prev[key],
              decision: "discard",
              matchedSkillName: matchedName,
            },
          }));
          message.success(`"${name}" matches existing skill "${matchedName}". Marked as Discard.`);
        } else {
          setEnrichmentDecisions((prev) => ({
            ...prev,
            [key]: {
              ...prev[key],
              decision: "add",
              matchedSkillName: undefined,
            },
          }));
          message.success(`"${name}" is new. Marked as Add.`);
        }
      }
    } catch (error) {
      console.error("Individual dedup error:", error);
      message.error("Failed to run deduplication for this skill.");
    } finally {
      setIndividualDeduping((prev) => ({ ...prev, [key]: false }));
    }
  };

  const filteredCourseItems = useMemo(() => {
    const items = jobDetail?.items || [];
    return items.filter((item) => {
      const matchesStatus = courseMonitorStatus === "all" ? true : item.status === courseMonitorStatus;
      const search = courseMonitorSearch.trim().toLowerCase();
      if (!search) return matchesStatus;
      const searchable = [item.courseTitle, item.courseId, item.error].filter(Boolean).join(" ").toLowerCase();
      return matchesStatus && searchable.includes(search);
    });
  }, [jobDetail?.items, courseMonitorSearch, courseMonitorStatus]);

  const itemColumns = useMemo(() => {
    const jobType = jobDetail?.job?.type || pipelineType;

    const baseCourseCol = {
      title: "Course",
      dataIndex: "courseTitle",
      key: "courseTitle",
      width: 280,
      ellipsis: true,
      render: (text, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {["Skills", "SkillsV2"].includes(jobType) ? (
            <Button
              type="link"
              style={{ padding: 0, fontWeight: 500, textAlign: "left", display: "inline-block", maxWidth: "85%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
              title={text}
              onClick={() => handleOpenManualModal(record.courseId, text)}
            >
              {text || "-"}
            </Button>
          ) : record.courseId ? (
            <a
              href={`/course/update/${record.courseId}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontWeight: 500, color: "inherit", maxWidth: "85%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
              title={text}
            >
              {text || "-"}
            </a>
          ) : (
            <span style={{ fontWeight: 500 }}>{text || "-"}</span>
          )}
          {record.courseId && (
            <a href={`/course/update/${record.courseId}`} target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center" }}>
              <LinkOutlined style={{ fontSize: 13, color: "#1677ff" }} title="Open Full Course Page" />
            </a>
          )}
        </div>
      ),
    };
    const statusCol = {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      align: "center",
      render: (status) => <Tag color={statusColor[status]}>{status === "cancelled" ? "paused" : status}</Tag>,
    };
    const tokensCol = {
      title: "Tokens",
      dataIndex: ["usage", "totalTokens"],
      key: "tokens",
      width: 80,
      align: "right",
      render: (val) => <Text>{val ? val.toLocaleString() : "—"}</Text>,
    };
    const errorCol = {
      title: "Error",
      dataIndex: "error",
      key: "error",
      width: 180,
      ellipsis: true,
      render: (text) => (text ? <Text type="danger" title={text} style={{ fontSize: 12 }}>{text}</Text> : null),
    };

    if (jobType === "LevelBulk") {
      return [
        baseCourseCol,
        statusCol,
        {
          title: "Assigned level",
          key: "level",
          width: 220,
          render: (_, row) => {
            const d = row.decisions;
            if (!d) return <Text type="secondary">—</Text>;
            return (
              <Space wrap>
                {d.levelName ? <Tag color="blue">{d.levelName}</Tag> : <Text type="secondary">—</Text>}
                {d.dryRun ? <Tag color="gold">preview</Tag> : null}
              </Space>
            );
          },
        },
        tokensCol,
        errorCol,
      ];
    }

    if (jobType === "MetaTagsBulk") {
      return [
        baseCourseCol,
        statusCol,
        {
          title: "Meta title",
          key: "meta",
          ellipsis: true,
          width: 260,
          render: (_, row) => {
            const t = row.decisions?.metaTitle;
            return t ? <Text ellipsis title={t}>{t}</Text> : <Text type="secondary">—</Text>;
          },
        },
        tokensCol,
        errorCol,
      ];
    }

    if (jobType === "FaqsBulk") {
      return [
        baseCourseCol,
        statusCol,
        {
          title: "FAQ questions",
          key: "faqs",
          width: 320,
          render: (_, row) => {
            const qs = (row.generatedSkills || []).slice(0, 5);
            return (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {qs.length
                  ? qs.map((q) => (
                    <Tag key={q} style={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{q}</Tag>
                  ))
                  : <Text type="secondary">—</Text>}
              </div>
            );
          },
        },
        tokensCol,
        errorCol,
      ];
    }

    return [
      baseCourseCol,
      statusCol,
      {
        title: "Skills",
        dataIndex: "generatedSkills",
        key: "generatedSkills",
        width: 320,
        render: (skills = [], row) => {
          const tags = (row.decisions?.autoLinked || skills || []).slice(0, 5);
          return (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {tags.length > 0
                ? tags.map((entry) => (
                  <Tag key={entry.skillId || entry} style={{ marginBottom: 2, maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {entry.skillName || entry}
                  </Tag>
                ))
                : <Text type="secondary">—</Text>}
              {(row.decisions?.autoLinked || skills || []).length > 5 && (
                <Tag style={{ background: "#f5f5f5", borderStyle: "dashed" }}>+{(row.decisions?.autoLinked || skills || []).length - 5}</Tag>
              )}
            </div>
          );
        },
      },
      tokensCol,
      errorCol,
    ];
  }, [jobDetail?.job?.type, pipelineType]);

  return (
    <Page title="AI Bulk Jobs">
      <div className="page-wrapper">
        <div className="content container-fluid">
          <PagesHeader pageTitle="AI Bulk Jobs" homePageUrl="/" homePageText="Dashboard" activePageText="AI Bulk Jobs" />

          <Row gutter={[24, 24]}>
            {/* Sidebar Column */}
            <Col xs={24} lg={6}>
              <Button
                type="primary"
                block
                size="large"
                icon={<PlusOutlined />}
                onClick={() => setIsCreateModalOpen(true)}
                style={{ marginBottom: 16, borderRadius: 6, height: 40 }}
              >
                New Bulk Job
              </Button>

              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "#718096", marginBottom: 6 }}>Pipeline View</div>
                <Select
                  style={{ width: "100%" }}
                  value={pipelineType}
                  onChange={(val) => {
                    setPipelineType(val);
                    setSelectedJobId(null);
                    setJobDetail(null);
                  }}
                  options={[
                    { label: "Skills — Inverted RAG (v2)", value: "SkillsV2" },
                    { label: "Skills — Manual review (v1)", value: "Skills" },
                    { label: "Level — classify & assign", value: "LevelBulk" },
                    { label: "Meta tags — SEO", value: "MetaTagsBulk" },
                    { label: "FAQs — generate", value: "FaqsBulk" },
                  ]}
                />
              </div>

              <Card title="Recent Jobs" size="small" style={{ borderRadius: 6 }} bodyStyle={{ padding: 0 }}>
                <div style={{ maxHeight: "calc(100vh - 280px)", overflowY: "auto", padding: "8px 16px 8px 12px" }}>
                  {jobs.length === 0 && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No jobs yet" />}
                  <Space direction="vertical" style={{ width: "100%", paddingRight: "6px" }} size={8}>
                    {jobs.map((job) => (
                      <div
                        key={job._id}
                        onClick={() => {
                          setSelectedJobId(job._id);
                          if (job.type) setPipelineType(job.type);
                        }}
                        style={{
                          padding: 12,
                          border: selectedJobId === job._id ? "1px solid #1677ff" : "1px solid #f0f0f0",
                          borderRadius: 6,
                          cursor: "pointer",
                          background: selectedJobId === job._id ? "#f0f7ff" : "#fff",
                          transition: "all 0.2s ease",
                        }}
                      >
                        <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: 6 }}>
                          <span style={{ fontWeight: 600, fontSize: 13, color: "#2d3748" }}>
                            {job.filters?.categoryName || "Bulk job"}
                          </span>
                          <Tag color={statusColor[job.status]} style={{ margin: 0, fontSize: 10 }}>{job.status === "cancelled" ? "paused" : job.status}</Tag>
                        </div>
                        <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: 4 }}>
                          <span style={{ fontSize: 11, color: "#718096" }}>{job.type || "Processor"}</span>
                          <span style={{ fontSize: 11, color: "#718096" }}>{job.totals?.total || 0} courses</span>
                        </div>
                        <Progress percent={percentFromTotals(job.totals)} size="small" showInfo={false} strokeColor="#1677ff" />
                      </div>
                    ))}
                  </Space>
                </div>
              </Card>
            </Col>

            {/* Main Content Workspace Column */}
            <Col xs={24} lg={18}>
              {!jobDetail?.job ? (
                <Card style={{ borderRadius: 6, minHeight: 500 }} bodyStyle={{ padding: "32px 24px" }}>
                  <div style={{ textAlign: "center", marginBottom: 32 }}>
                    <h2 style={{ fontSize: 22, fontWeight: 600, color: "#1a202c", marginBottom: 8 }}>Welcome to AI Bulk Jobs</h2>
                    <p style={{ color: "#718096", fontSize: 14 }}>Select a pipeline processor below to view its recent execution runs or create a new bulk job.</p>
                  </div>
                  <Row gutter={[16, 16]}>
                    {[
                      { label: "Skills — Inverted RAG (v2)", value: "SkillsV2", desc: "Tag courses with matching skills using taxonomy and advanced RAG.", color: "#3182ce", icon: "🧠" },
                      { label: "Skills — Manual review (v1)", value: "Skills", desc: "Original skills mapping pipeline with human confirmation loop.", color: "#4a5568", icon: "👤" },
                      { label: "Level — classify & assign", value: "LevelBulk", desc: "Analyze curriculum depth to automatically classify and assign course difficulty level.", color: "#38a169", icon: "📊" },
                      { label: "Meta tags — SEO", value: "MetaTagsBulk", desc: "Generate search engine optimized meta titles and descriptions.", color: "#805ad5", icon: "🔍" },
                      { label: "FAQs — generate", value: "FaqsBulk", desc: "Auto-generate curriculum-based frequently asked questions for courses.", color: "#dd6b20", icon: "❓" },
                    ].map((item) => (
                      <Col xs={24} sm={12} md={8} key={item.value}>
                        <div
                          onClick={() => {
                            setPipelineType(item.value);
                            setSelectedJobId(null);
                            setJobDetail(null);
                          }}
                          style={{
                            padding: "20px 16px",
                            border: pipelineType === item.value ? `2px solid ${item.color}` : "1px solid #e2e8f0",
                            borderRadius: 8,
                            cursor: "pointer",
                            background: pipelineType === item.value ? "#f7fafc" : "#fff",
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            transition: "all 0.2s ease-in-out",
                            boxShadow: pipelineType === item.value ? "0 4px 6px -1px rgba(0,0,0,0.1)" : "none",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = item.color;
                            e.currentTarget.style.transform = "translateY(-2px)";
                          }}
                          onMouseLeave={(e) => {
                            if (pipelineType !== item.value) {
                              e.currentTarget.style.borderColor = "#e2e8f0";
                            }
                            e.currentTarget.style.transform = "none";
                          }}
                        >
                          <div>
                            <div style={{ fontSize: 24, marginBottom: 8 }}>{item.icon}</div>
                            <h4 style={{ fontSize: 14, fontWeight: 600, color: "#2d3748", marginBottom: 6 }}>{item.label}</h4>
                            <p style={{ fontSize: 11, color: "#718096", lineHeight: 1.4, margin: 0 }}>{item.desc}</p>
                          </div>
                          <div style={{ marginTop: 12, display: "flex", justifyContent: "flex-end" }}>
                            {pipelineType === item.value && (
                              <Tag color="blue" style={{ margin: 0 }}>Selected</Tag>
                            )}
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </Card>
              ) : (
                <Space direction="vertical" size={16} style={{ width: "100%" }}>
                  {/* Job Header Info Card */}
                  <Card size="small" style={{ borderRadius: 6 }} bodyStyle={{ padding: "16px 20px" }}>
                    <div className="d-flex justify-content-between align-items-center flex-wrap" style={{ gap: 12 }}>
                      <div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
                            {jobDetail.job.filters?.categoryName || "AI Bulk Job"}
                          </h2>
                          <Tag style={{ margin: 0 }}>{jobDetail.job.type}</Tag>
                          <Tag color={statusColor[jobDetail.job.status]} style={{ margin: 0 }}>{jobDetail.job.status === "cancelled" ? "paused" : jobDetail.job.status}</Tag>
                        </div>
                        {jobDetail.job.configSnapshot && (
                          <div style={{ marginTop: 4, fontSize: 12, color: "#718096" }}>
                            Model: {jobDetail.job.configSnapshot.model || "—"}
                            {" · "}Temp: {jobDetail.job.configSnapshot.temperature ?? "—"}
                            {" · "}Tokens: {jobDetail.job.configSnapshot.maxTokens ?? "—"}
                            {jobDetail.job.configSnapshot.skipIfSkillsExist ? " · skipped existing" : ""}
                            {jobDetail.job.configSnapshot.dryRun ? " · dry run" : ""}
                          </div>
                        )}
                      </div>
                      <Space>
                        {jobDetail.job.status !== "running" && jobDetail.job.status !== "completed" && (
                          <Button type="primary" icon={<PlayCircleOutlined />} onClick={runJob}>
                            {jobDetail.job.status === "cancelled" ? "Resume" : "Run"}
                          </Button>
                        )}
                        {jobDetail.job.status === "running" && (
                          <Button icon={<PauseCircleOutlined />} onClick={pauseJob} style={{ borderColor: "#faad14", color: "#faad14" }}>
                            Pause
                          </Button>
                        )}
                      </Space>
                    </div>

                    <div style={{ marginTop: 16 }}>
                      <div className="d-flex justify-content-between align-items-center" style={{ fontSize: 12, color: "#718096", marginBottom: 4 }}>
                        <span>Overall Progress ({percentFromTotals(jobDetail.job.totals)}%)</span>
                        <span>{jobDetail.job.usage?.totalTokens?.toLocaleString() || 0} tokens used</span>
                      </div>
                      <Progress percent={percentFromTotals(jobDetail.job.totals)} strokeColor="#52c41a" />
                    </div>

                    <Row gutter={[12, 12]} style={{ marginTop: 16 }}>
                      {[
                        { label: "Total", value: jobDetail.job.totals?.total, color: "#4a5568" },
                        { label: "Pending", value: jobDetail.job.totals?.pending, color: "#3182ce" },
                        { label: "Generating", value: jobDetail.job.totals?.generating, color: "#d69e2e" },
                        { label: "Review Required", value: jobDetail.job.totals?.needsReview, color: "#e53e3e" },
                        { label: "Saved", value: jobDetail.job.totals?.saved, color: "#38a169" },
                        { label: "Failed", value: jobDetail.job.totals?.failed, color: "#e53e3e" },
                      ].map((item) => (
                        <Col xs={12} sm={8} md={4} key={item.label}>
                          <div style={{ background: "#fafafa", border: "1px solid #f0f0f0", borderRadius: 6, padding: "8px 12px" }}>
                            <div style={{ fontSize: 11, color: "#718096" }}>{item.label}</div>
                            <div style={{ fontSize: 18, fontWeight: 600, color: item.color }}>{item.value || 0}</div>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </Card>

                  {/* Main Work Tabs */}
                  <Card style={{ borderRadius: 6 }} bodyStyle={{ padding: "8px 16px" }}>
                    <Tabs
                      defaultActiveKey="insights"
                      tabBarExtraContent={
                        <Space>
                          <Button icon={<ReloadOutlined />} onClick={() => { loadJobs(); loadJobDetail(); }} loading={loadingDetail}>
                            Refresh
                          </Button>
                          {jobDetail.job.status !== "running" && (jobDetail.job.totals?.failed || 0) > 0 && (
                            <Button type="primary" danger icon={<RedoOutlined />} onClick={retryFailed} loading={retrying}>
                              Retry Failed
                            </Button>
                          )}
                          {jobDetail?.job && (
                            <>
                              <Button type="default" icon={<ExpandOutlined />} onClick={() => setIsCourseMonitorOpen(true)}>
                                Full Monitor
                              </Button>
                              <Button type="default" icon={<DownloadOutlined />} onClick={handleExportJSON}>
                                Export JSON
                              </Button>
                            </>
                          )}
                        </Space>
                      }
                      items={[
                        {
                          key: "insights",
                          label: "Insights & Calibration",
                          children: (
                            <div style={{ paddingTop: 16 }}>
                              {jobDetail.job.type === "SkillsV2" && (
                                <Row gutter={[16, 16]}>
                                  {[
                                    { label: "SYNONYM Predicted", value: jobDetail.job.calibration?.synonymPredicted },
                                    { label: "HYPONYM Predicted", value: jobDetail.job.calibration?.hyponymPredicted },
                                    { label: "DISTINCT Matches", value: jobDetail.job.calibration?.distinctCount },
                                    { label: "UNCERTAIN Output", value: jobDetail.job.calibration?.uncertainCount },
                                    { label: "Auto-Linked Total", value: jobDetail.job.calibration?.autoLinkedTotal },
                                    { label: "Held by Score Floor", value: jobDetail.job.calibration?.heldByScoreFloor },
                                    { label: "Held by Cap Limits", value: jobDetail.job.calibration?.heldByCap },
                                    { label: "Missing Taxonomy Gaps", value: jobDetail.job.calibration?.missingCount },
                                    { label: "Avg Auto-Links/Course", value: jobDetail.job.calibration?.avgAutoLinkedPerCourse },
                                  ].map((c) => (
                                    <Col xs={24} sm={12} md={8} key={c.label}>
                                      <div style={{ padding: 12, border: "1px solid #f0f0f0", borderRadius: 6, background: "#fafafa" }}>
                                        <div style={{ fontSize: 11, color: "#718096" }}>{c.label}</div>
                                        <div style={{ fontSize: 16, fontWeight: 600, marginTop: 4 }}>{c.value ?? 0}</div>
                                      </div>
                                    </Col>
                                  ))}
                                </Row>
                              )}

                              {jobDetail.job.type === 'LevelBulk' && (() => {
                                const items = jobDetail.items || [];
                                const levelDist = {};
                                let agreeCount = 0;
                                let disagreeCount = 0;
                                let lowConfCount = 0;
                                let totalWithPrev = 0;
                                items.forEach((item) => {
                                  const d = item.decisions || {};
                                  if (d.levelName) levelDist[d.levelName] = (levelDist[d.levelName] || 0) + 1;
                                  if (d.previousLevelName) {
                                    totalWithPrev += 1;
                                    if (d.previousLevelName === d.levelName) agreeCount += 1;
                                    else disagreeCount += 1;
                                  }
                                  if (d.confidence !== undefined && d.confidence < 0.70) lowConfCount += 1;
                                });
                                const agreePct = totalWithPrev > 0 ? Math.round((agreeCount / totalWithPrev) * 100) : 0;
                                return (
                                  <>
                                    <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                                      {[
                                        { label: 'Agreement with Existing', value: totalWithPrev > 0 ? `${agreePct}% (${agreeCount}/${totalWithPrev})` : 'N/A' },
                                        { label: 'Disagreements', value: disagreeCount },
                                        { label: 'Low Confidence Items', value: lowConfCount },
                                        { label: 'Total Classified', value: items.filter(i => i.decisions?.levelName).length },
                                      ].map((c) => (
                                        <Col xs={24} sm={12} md={6} key={c.label}>
                                          <div style={{ padding: 12, border: '1px solid #f0f0f0', borderRadius: 6, background: '#fafafa' }}>
                                            <div style={{ fontSize: 11, color: '#718096' }}>{c.label}</div>
                                            <div style={{ fontSize: 16, fontWeight: 600, marginTop: 4 }}>{c.value ?? 0}</div>
                                          </div>
                                        </Col>
                                      ))}
                                    </Row>
                                    {Object.keys(levelDist).length > 0 && (
                                      <div style={{ padding: 12, border: '1px solid #f0f0f0', borderRadius: 6, background: '#fafafa' }}>
                                        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>AI Level Distribution</div>
                                        {Object.entries(levelDist).sort((a, b) => b[1] - a[1]).map(([name, count]) => (
                                          <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
                                            <span style={{ width: 100, fontSize: 12, flexShrink: 0 }}>{name}</span>
                                            <Progress percent={Math.round((count / items.length) * 100)} size="small" style={{ flex: 1, margin: 0 }} showInfo={false} />
                                            <span style={{ width: 120, fontSize: 12, textAlign: "right", color: "#4a5568", flexShrink: 0 }}>
                                              {count} courses ({Math.round((count / items.length) * 100)}%)
                                            </span>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </>
                                );
                              })()}

                              {CONTENT_PROCESSOR_TYPES.includes(jobDetail.job.type) && (
                                <Row gutter={[16, 16]}>
                                  {[
                                    { label: "Preview (Dry Run) Courses", value: jobDetail.job.processorStats?.previewCount },
                                    { label: "DB Writes Applied", value: jobDetail.job.processorStats?.dbWriteCount },
                                    { label: "FAQ Rows Written", value: jobDetail.job.processorStats?.faqItemsWrittenTotal },
                                  ].map((c) => (
                                    <Col xs={24} sm={12} md={8} key={c.label}>
                                      <div style={{ padding: 12, border: "1px solid #f0f0f0", borderRadius: 6, background: "#fafafa" }}>
                                        <div style={{ fontSize: 11, color: "#718096" }}>{c.label}</div>
                                        <div style={{ fontSize: 16, fontWeight: 600, marginTop: 4 }}>{c.value ?? 0}</div>
                                      </div>
                                    </Col>
                                  ))}
                                </Row>
                              )}
                            </div>
                          ),
                        },
                        {
                          key: "monitor",
                          label: "Course Monitor",
                          children: (
                            <div style={{ paddingTop: 8 }}>
                              <Row gutter={[12, 12]} align="middle" style={{ marginBottom: 16 }}>
                                <Col xs={24} md={16}>
                                  <Input
                                    allowClear
                                    placeholder="Search course title / id / error"
                                    value={courseMonitorSearch}
                                    onChange={(event) => setCourseMonitorSearch(event.target.value)}
                                    style={{ width: "100%" }}
                                  />
                                </Col>
                                <Col xs={24} md={8}>
                                  <Select
                                    style={{ width: "100%" }}
                                    value={courseMonitorStatus}
                                    onChange={setCourseMonitorStatus}
                                    options={[
                                      { value: "all", label: "All statuses" },
                                      { value: "pending", label: "Pending" },
                                      { value: "generating", label: "Generating" },
                                      { value: "needs_review", label: "Needs Review" },
                                      { value: "saved", label: "Saved" },
                                      { value: "failed", label: "Failed" },
                                      { value: "skipped", label: "Skipped" },
                                    ]}
                                  />
                                </Col>
                              </Row>

                              <Table
                                rowKey="_id"
                                size="middle"
                                tableLayout="fixed"
                                columns={itemColumns}
                                dataSource={filteredCourseItems}
                                pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: [10, 20, 50] }}
                                scroll={{ x: 960 }}
                                expandable={{
                                  expandedRowRender: (record) => {
                                    const jt = jobDetail.job.type;
                                    if (jt === "SkillsV2") {
                                      const autoLinked = record.decisions?.autoLinked || [];
                                      const held = record.decisions?.held || [];
                                      const missing = record.decisions?.missing || [];
                                      return (
                                        <div>
                                          <div style={{ marginBottom: 8 }}>
                                            <Text strong>Candidates: </Text>
                                            <Text>{record.candidateSkills?.length || 0}</Text>
                                          </div>
                                          <div style={{ marginBottom: 8 }}>
                                            <Text strong>Auto-linked: </Text>
                                            {autoLinked.length ? autoLinked.map((entry) => <Tag key={entry.skillId}>{entry.skillName}</Tag>) : <Text type="secondary">None</Text>}
                                          </div>
                                          <div style={{ marginBottom: 8 }}>
                                            <Text strong>Held: </Text>
                                            {held.length
                                              ? held.map((entry, idx) => (
                                                  <Tag key={`${entry.skillId}-${idx}`} color="orange">
                                                    {entry.skillName || entry.skillId} ({entry.reason})
                                                  </Tag>
                                                ))
                                              : <Text type="secondary">None</Text>}
                                          </div>
                                          <div style={{ marginTop: 12 }}>
                                            <div style={{ fontWeight: 600, marginBottom: 8 }}>Missing Taxonomy Gaps</div>
                                            <V2MissingSkillsReview
                                              item={record}
                                              jobId={selectedJobId}
                                              missing={missing}
                                              skillOptions={skillOptions}
                                              onSaved={() => {
                                                loadSkills();
                                                loadJobs();
                                                loadJobDetail(selectedJobId);
                                              }}
                                            />
                                          </div>
                                        </div>
                                      );
                                    }
                                    if (jt === "LevelBulk") {
                                      const d = record.decisions || {};
                                      const scores = d.scores || {};
                                      const disagreement = d.previousLevelName && d.levelName && d.previousLevelName !== d.levelName;
                                      return (
                                        <Space direction="vertical" size={12} style={{ width: '100%' }}>
                                          <Row gutter={[16, 8]}>
                                            <Col xs={24} md={12}>
                                              <div style={{ padding: 12, border: '1px solid #f0f0f0', borderRadius: 6, background: '#fafafa' }}>
                                                <div style={{ fontSize: 11, color: '#718096', marginBottom: 4 }}>Current Level</div>
                                                <div style={{ fontSize: 15, fontWeight: 600 }}>{d.previousLevelName || <Text type="secondary">Not assigned</Text>}</div>
                                              </div>
                                            </Col>
                                            <Col xs={24} md={12}>
                                              <div style={{ padding: 12, border: `1px solid ${disagreement ? '#ffa39e' : '#b7eb8f'}`, borderRadius: 6, background: disagreement ? '#fff2f0' : '#f6ffed' }}>
                                                <div style={{ fontSize: 11, color: '#718096', marginBottom: 4 }}>AI Suggests</div>
                                                <div style={{ fontSize: 15, fontWeight: 600 }}>
                                                  {d.levelName || d.levelId}
                                                  {d.dryRun && <Tag color="gold" style={{ marginLeft: 8 }}>preview</Tag>}
                                                  {d.autoApplied === false && !d.dryRun && <Tag color="orange" style={{ marginLeft: 8 }}>low confidence</Tag>}
                                                </div>
                                              </div>
                                            </Col>
                                          </Row>
                                          {(() => {
                                             const contextText = d.levelSummary || d.level_summary || d.level_context || record.llmOutput?.parsed?.level_summary || record.llmOutput?.parsed?.levelSummary || record.llmOutput?.parsed?.level_context || record.llmOutput?.parsed?.levelContext;
                                             return contextText ? (
                                               <div style={{ padding: 12, border: '1px solid #e2e8f0', borderRadius: 6, background: '#f8fafc' }}>
                                                 <div style={{ fontSize: 11, color: '#718096', marginBottom: 4 }}>AI Level Summary (For Learner)</div>
                                                 <div style={{ fontSize: 13, fontStyle: 'italic', color: '#2d3748' }}>"{contextText}"</div>
                                               </div>
                                             ) : null;
                                           })()}
                                          {Object.keys(scores).length > 0 && (
                                            <div style={{ padding: 12, border: '1px solid #f0f0f0', borderRadius: 6, background: '#fafafa' }}>
                                              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Confidence Scores</div>
                                              {Object.entries(scores).sort((a, b) => b[1] - a[1]).map(([levelName, score]) => {
                                                const isCurrent = (d.levelName || "").toLowerCase() === levelName.toLowerCase();
                                                return (
                                                  <div key={levelName} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
                                                    <span style={{ width: 100, fontSize: 12, fontWeight: isCurrent ? 600 : 400, color: isCurrent ? '#1677ff' : '#4a5568', textTransform: 'capitalize', flexShrink: 0 }}>
                                                      {levelName}
                                                    </span>
                                                    <Progress percent={Math.round(score * 100)} size="small" style={{ flex: 1, margin: 0 }} strokeColor={isCurrent ? '#1677ff' : '#d9d9d9'} showInfo={false} />
                                                    <span style={{ width: 45, fontSize: 12, fontWeight: isCurrent ? 600 : 400, color: isCurrent ? '#1677ff' : '#718096', textAlign: 'right', flexShrink: 0 }}>
                                                      {Math.round(score * 100)}%
                                                    </span>
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          )}
                                          <Collapse ghost>
                                            <Collapse.Panel header="Raw LLM Output" key="1">
                                              <pre style={{ whiteSpace: 'pre-wrap', fontSize: 11, maxHeight: 220, overflow: 'auto', margin: 0, background: '#f8fafc', padding: 8, borderRadius: 4 }}>
                                                {JSON.stringify(record.llmOutput?.parsed || record.llmOutput, null, 2)}
                                              </pre>
                                            </Collapse.Panel>
                                          </Collapse>
                                        </Space>
                                      );
                                    }
                                    if (jt === "MetaTagsBulk") {
                                      const d = record.decisions || {};
                                      return (
                                        <Space direction="vertical" style={{ width: "100%" }}>
                                          <div><Text strong>Meta title</Text><div>{d.metaTitle}</div></div>
                                          <div><Text strong>Meta description</Text><div style={{ whiteSpace: "pre-wrap" }}>{d.metaDescription}</div></div>
                                          {d.dryRun ? <Tag color="gold">preview — not written to DB</Tag> : null}
                                          <pre style={{ whiteSpace: "pre-wrap", fontSize: 11, maxHeight: 220, overflow: "auto", margin: 0, background: "#fff", padding: 8, borderRadius: 4 }}>
                                            {JSON.stringify(record.llmOutput?.parsed || record.llmOutput, null, 2)}
                                          </pre>
                                        </Space>
                                      );
                                    }
                                    if (jt === "FaqsBulk") {
                                      const preview = record.decisions?.faqsPreview || [];
                                      return (
                                        <Space direction="vertical" style={{ width: "100%" }}>
                                          {preview.map((faq, i) => (
                                            <Card key={i} size="small" title={<Text strong>{faq.question}</Text>}>
                                              <div style={{ whiteSpace: "pre-wrap", fontSize: 13 }}>{faq.answer}</div>
                                            </Card>
                                          ))}
                                          {record.decisions?.replaceFaqs && <Tag>replace mode</Tag>}
                                          {record.decisions?.dryRun ? <Tag color="gold">preview</Tag> : null}
                                        </Space>
                                      );
                                    }
                                    return <Text type="secondary">Expand is only populated for supported processors.</Text>;
                                  },
                                }}
                              />
                            </div>
                          ),
                        },
                        ...(needsReviewItems.length > 0
                          ? [
                              {
                                key: "review",
                                label: (
                                  <span>
                                    Review Queue{" "}
                                    <Badge
                                      count={needsReviewItems.length}
                                      size="small"
                                      style={{ backgroundColor: "#e53e3e", marginLeft: 4 }}
                                    />
                                  </span>
                                ),
                                children: (
                                  <div style={{ paddingTop: 16 }}>
                                    {needsReviewItems.map((item) => (
                                      <BulkSkillsReviewItem
                                        key={item._id}
                                        item={item}
                                        skillOptions={skillOptions}
                                        onSaved={() => {
                                          loadSkills();
                                          loadJobs();
                                          loadJobDetail(selectedJobId);
                                        }}
                                      />
                                    ))}
                                  </div>
                                ),
                              },
                            ]
                          : []),
                        ...(jobDetail.job.type === "SkillsV2" && aggregatedMissingSkills.length > 0
                          ? [
                              {
                                key: "enrichment",
                                label: (
                                  <span>
                                    Skill Enrichment{" "}
                                    <Badge
                                      count={aggregatedMissingSkills.length}
                                      size="small"
                                      style={{ backgroundColor: "#d48806", marginLeft: 4 }}
                                    />
                                  </span>
                                ),
                                children: (
                                  <div style={{ paddingTop: 16 }}>
                                    <div style={{ marginBottom: 16 }}>
                                      <Text type="secondary" style={{ fontSize: 13 }}>
                                        These are all unique missing taxonomy gaps found across the bulk run. Skills already committed per-course are excluded.
                                        Add or discard skills here and commit once to apply across all affected courses.
                                      </Text>
                                    </div>
                                    <div style={{ marginBottom: 16, display: "flex", justifyContent: "flex-end", alignItems: "center", padding: "10px 12px", background: "#fafafa", borderRadius: 6, border: "1px solid #f0f0f0" }}>
                                      <Space>
                                        <Button
                                          size="small"
                                          onClick={() => {
                                            const all = {};
                                            aggregatedMissingSkills.forEach((s) => { all[s.key] = { ...(enrichmentDecisions[s.key] || {}), decision: "add" }; });
                                            setEnrichmentDecisions(all);
                                          }}
                                        >
                                          Add All
                                        </Button>
                                        <Button
                                          size="small"
                                          icon={<FilterOutlined />}
                                          loading={enrichmentDeduping}
                                          onClick={dedupEnrichment}
                                          style={{ borderColor: "#722ed1", color: "#722ed1" }}
                                        >
                                          Dedup All
                                        </Button>
                                        <Button
                                          size="small"
                                          onClick={() => setEnrichmentDecisions({})}
                                        >
                                          Reset
                                        </Button>
                                        <Button
                                          type="primary"
                                          icon={<SaveOutlined />}
                                          loading={enrichmentSaving}
                                          disabled={!Object.values(enrichmentDecisions).some((d) => d.decision)}
                                          onClick={commitEnrichment}
                                        >
                                          Commit Enrichment ({Object.values(enrichmentDecisions).filter((d) => d.decision).length} decisions)
                                        </Button>
                                      </Space>
                                    </div>
                                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                                      {aggregatedMissingSkills.map((skill) => {
                                        const dec = enrichmentDecisions[skill.key] || {};
                                        const decided = dec.decision === "add" || dec.decision === "discard";
                                        return (
                                          <div
                                            key={skill.key}
                                            style={{
                                              padding: 12,
                                              border: `1px solid ${dec.decision === "add" ? "#b7eb8f" : dec.decision === "discard" ? "#ffa39e" : "#f0f0f0"}`,
                                              borderRadius: 6,
                                              background: dec.decision === "add" ? "#f6ffed" : dec.decision === "discard" ? "#fff2f0" : "#fff",
                                              transition: "all 0.2s ease",
                                            }}
                                          >
                                            <Row gutter={[10, 10]} align="middle">
                                              <Col xs={24} md={7}>
                                                <Input
                                                  value={dec.name ?? skill.name}
                                                  onChange={(e) => updateEnrichmentDecision(skill.key, "name", e.target.value)}
                                                  placeholder="Skill name"
                                                  style={{ width: "100%" }}
                                                  disabled={dec.decision === "discard"}
                                                />
                                              </Col>
                                              <Col xs={24} md={7}>
                                                <Select
                                                  allowClear
                                                  showSearch
                                                  optionFilterProp="label"
                                                  value={dec.parentId ?? skill.parentId}
                                                  onChange={(value) => updateEnrichmentDecision(skill.key, "parentId", value)}
                                                  options={skillOptions}
                                                  placeholder="Parent skill"
                                                  style={{ width: "100%" }}
                                                  disabled={dec.decision === "discard"}
                                                />
                                              </Col>
                                              <Col xs={24} md={4}>
                                                <Tag color="gold" style={{ margin: 0 }}>
                                                  {skill.sources.length} course{skill.sources.length !== 1 ? "s" : ""}
                                                </Tag>
                                              </Col>
                                              <Col xs={24} md={6}>
                                                <Space>
                                                  <Button
                                                    size="small"
                                                    icon={<FilterOutlined />}
                                                    loading={individualDeduping[skill.key]}
                                                    style={{ borderColor: "#722ed1", color: "#722ed1" }}
                                                    onClick={() => dedupIndividualEnrichment(skill.key, dec.name ?? skill.name)}
                                                    disabled={dec.decision === "discard"}
                                                  >
                                                    Dedup
                                                  </Button>
                                                  <Button
                                                    size="small"
                                                    icon={<CheckCircleOutlined />}
                                                    type={dec.decision === "add" ? "primary" : "default"}
                                                    style={dec.decision === "add" ? { background: "#52c41a", borderColor: "#52c41a" } : {}}
                                                    onClick={() => updateEnrichmentDecision(skill.key, "decision", dec.decision === "add" ? undefined : "add")}
                                                  >
                                                    Add
                                                  </Button>
                                                </Space>
                                              </Col>
                                            </Row>
                                            {(dec.matchedSkillName || skill.reasoning) && (
                                              <div style={{ marginTop: 6, fontSize: 12, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                                                {dec.matchedSkillName && (
                                                  <Tag color="purple" style={{ margin: 0, fontWeight: 500 }}>
                                                    Allotted to: {dec.matchedSkillName}
                                                  </Tag>
                                                )}
                                                {skill.reasoning && (
                                                  <Text type="secondary">{skill.reasoning}</Text>
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                ),
                              },
                            ]
                          : []),
                      ]}
                    />
                  </Card>
                </Space>
              )}
            </Col>
          </Row>

          {/* Create Job Drawer */}
          <Drawer
            title={<span style={{ fontWeight: 600, fontSize: 16 }}>Create Bulk Job</span>}
            placement="right"
            width={480}
            open={isCreateModalOpen}
            onClose={() => setIsCreateModalOpen(false)}
            destroyOnClose
          >
            <Space direction="vertical" style={{ width: "100%" }} size={16}>
              <div>
                <div className="d-flex justify-content-between align-items-center">
                  <Text type="secondary">Processor</Text>
                  {aiSettingsCategoryForProcessor(pipelineType) && (
                    <Button
                      type="link"
                      size="small"
                      icon={<SettingOutlined />}
                      onClick={() => setBulkV2ConfigVisible(true)}
                      loading={loadingAiDefaults}
                      style={{ padding: 0, height: "auto" }}
                    >
                      Default Settings
                    </Button>
                  )}
                </div>
                <Select
                  style={{ width: "100%", marginTop: 6 }}
                  value={pipelineType}
                  onChange={setPipelineType}
                  options={[
                    { label: "Skills — Inverted RAG (API v2)", value: "SkillsV2" },
                    { label: "Skills — Manual review (API v1)", value: "Skills" },
                    { label: "Level — classify & assign", value: "LevelBulk" },
                    { label: "Meta tags — SEO title + description", value: "MetaTagsBulk" },
                    { label: "FAQs — generate (append or replace)", value: "FaqsBulk" },
                  ]}
                />
              </div>

              <div>
                <Text type="secondary">Category</Text>
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={categories}
                  value={categoryId}
                  onChange={setCategoryId}
                  placeholder="Select category"
                  style={{ width: "100%", marginTop: 6 }}
                />
              </div>

              <div>
                <Text type="secondary">Course limit</Text>
                <InputNumber min={1} max={1000} value={limit} onChange={(value) => setLimit(value)} style={{ width: "100%", marginTop: 6 }} />
              </div>

              <div>
                <Text type="secondary">Batch size</Text>
                <InputNumber min={1} max={50} value={batchSize} onChange={(value) => setBatchSize(value)} style={{ width: "100%", marginTop: 6 }} />
              </div>

              <div style={{ marginTop: 8 }}>
                <Checkbox checked={skipIfSkillsExist} onChange={(e) => setSkipIfSkillsExist(e.target.checked)}>
                  {pipelineType === 'LevelBulk' ? 'Skip courses that already have a level assigned' :
                   pipelineType === 'MetaTagsBulk' ? 'Skip courses that already have SEO meta tags' :
                   pipelineType === 'FaqsBulk' ? 'Skip courses that already have FAQs' :
                   'Skip courses that already have skills'}
                </Checkbox>
              </div>

              {pipelineType === "SkillsV2" && (
                <>
                  <div>
                    <Text type="secondary">Top K candidates</Text>
                    <InputNumber min={1} max={100} value={topK} onChange={(value) => setTopK(value)} style={{ width: "100%", marginTop: 6 }} />
                  </div>
                  <div>
                    <Text type="secondary">Score floor</Text>
                    <InputNumber min={0} max={1} step={0.01} value={scoreFloor} onChange={(value) => setScoreFloor(value)} style={{ width: "100%", marginTop: 6 }} />
                  </div>
                  <div>
                    <Text type="secondary">Max auto-links / course</Text>
                    <InputNumber
                      min={1}
                      max={50}
                      value={maxAutoLinksPerCourse}
                      onChange={(value) => setMaxAutoLinksPerCourse(value)}
                      style={{ width: "100%", marginTop: 6 }}
                    />
                  </div>
                </>
              )}

              {CONTENT_PROCESSOR_TYPES.includes(pipelineType) && (
                <>
                  <div style={{ marginBottom: 12 }}>
                    <Text type="secondary">Workers AI model id</Text>
                    <Select
                      style={{ width: "100%", marginTop: 6 }}
                      placeholder="Select Model"
                      value={bulkModel}
                      onChange={(val) => setBulkModel(val)}
                      showSearch
                      options={availableModels.map((m) => ({ label: m, value: m }))}
                    />
                  </div>
                  <div>
                    <Text type="secondary">Temperature</Text>
                    <InputNumber min={0} max={1.5} step={0.05} value={bulkTemperature} onChange={(v) => setBulkTemperature(v)} style={{ width: "100%", marginTop: 6 }} />
                  </div>
                  <div>
                    <Text type="secondary">Max output tokens</Text>
                    <InputNumber min={64} max={8192} value={bulkMaxTokens} onChange={(v) => setBulkMaxTokens(v)} style={{ width: "100%", marginTop: 6 }} />
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <Checkbox checked={bulkDryRun} onChange={(e) => setBulkDryRun(e.target.checked)}>
                      Preview only (dry run, no DB write)
                    </Checkbox>
                  </div>
                  {pipelineType === "FaqsBulk" && (
                    <>
                      <div>
                        <Text type="secondary">FAQ count</Text>
                        <InputNumber min={1} max={20} value={bulkFaqCount} onChange={(v) => setBulkFaqCount(v)} style={{ width: "100%", marginTop: 6 }} />
                      </div>
                      <div style={{ marginTop: 8 }}>
                        <Checkbox checked={bulkReplaceFaqs} onChange={(e) => setBulkReplaceFaqs(e.target.checked)}>
                          Replace all existing FAQs
                        </Checkbox>
                      </div>
                    </>
                  )}
                  <Button block onClick={loadAiDefaultsForProcessor} loading={loadingAiDefaults} style={{ marginTop: 8 }}>
                    Reload from AI settings
                  </Button>
                  <Collapse
                    size="small"
                    items={[
                      {
                        key: "adv",
                        label: "Prompts & context (optional overrides)",
                        children: (
                          <Space direction="vertical" style={{ width: "100%" }} size={8}>
                            <Text type="secondary">Context fields</Text>
                            <Select
                              mode="multiple"
                              allowClear
                              style={{ width: "100%" }}
                              placeholder="Use saved defaults if empty"
                              value={bulkContextFields}
                              onChange={setBulkContextFields}
                              options={[
                                { value: "title", label: "Title" },
                                { value: "headlines", label: "Headline" },
                                { value: "description", label: "Description" },
                                { value: "out_come", label: "Outcomes" },
                                { value: "curriculum", label: "Curriculum" },
                                { value: "category", label: "Category" },
                                { value: "level", label: "Current level (hint)" },
                              ].filter(opt => !(pipelineType === "LevelBulk" && opt.value === "level"))}
                            />
                            <Text type="secondary">System prompt</Text>
                            <Input.TextArea rows={3} value={bulkSystemPrompt} onChange={(e) => setBulkSystemPrompt(e.target.value)} placeholder="Override for this job only" />
                            <Text type="secondary">User prompt</Text>
                            <Input.TextArea rows={2} value={bulkUserPrompt} onChange={(e) => setBulkUserPrompt(e.target.value)} />
                            <Text type="secondary">Internal instructions (JSON contract)</Text>
                            <Input.TextArea rows={3} value={bulkInternalInstructions} onChange={(e) => setBulkInternalInstructions(e.target.value)} />
                          </Space>
                        ),
                      },
                    ]}
                  />
                </>
              )}
              <Button type="primary" block icon={<PlayCircleOutlined />} loading={creating} onClick={createJob} style={{ height: 40, borderRadius: 6, marginTop: 12 }}>
                Create & Start
              </Button>
            </Space>
          </Drawer>

          <Drawer
            title={<Text strong style={{ fontSize: 16 }}>{`Course Monitor — ${jobDetail?.job?.filters?.categoryName || jobDetail?.job?.type || "Bulk Job"}`}</Text>}
            placement="right"
            width="96vw"
            open={isCourseMonitorOpen}
            onClose={() => setIsCourseMonitorOpen(false)}
            destroyOnClose
            styles={{ body: { padding: "16px 18px", background: "#fafafa", overflowX: "hidden" } }}
            extra={
              <Space>
                {jobDetail?.job?.status !== "running" && (jobDetail?.job?.totals?.failed || 0) > 0 && (
                  <Button type="primary" danger icon={<RedoOutlined />} onClick={retryFailed} loading={retrying}>
                    Retry Failed
                  </Button>
                )}
                {jobDetail?.job && (
                  <Button type="default" icon={<DownloadOutlined />} onClick={handleExportJSON}>
                    Export JSON
                  </Button>
                )}
                <Button type="primary" onClick={() => setIsCourseMonitorOpen(false)}>
                  Close Monitor
                </Button>
              </Space>
            }
            footer={
              <div style={{ display: "flex", justifyContent: "flex-end", padding: "10px 16px" }}>
                <Button type="primary" onClick={() => setIsCourseMonitorOpen(false)}>
                  Close Monitor
                </Button>
              </div>
            }
          >
            <Space direction="vertical" size={16} style={{ width: "100%" }}>
              <Card size="small" style={{ borderRadius: 6 }} bodyStyle={{ padding: "12px 16px" }}>
                <Row gutter={[12, 12]} align="middle">
                  <Col xs={24} md={16}>
                    <Input
                      allowClear
                      placeholder="Search course title / id / error"
                      value={courseMonitorSearch}
                      onChange={(event) => setCourseMonitorSearch(event.target.value)}
                      style={{ width: "100%" }}
                    />
                  </Col>
                  <Col xs={24} md={8}>
                    <Select
                      style={{ width: "100%" }}
                      value={courseMonitorStatus}
                      onChange={setCourseMonitorStatus}
                      options={[
                        { value: "all", label: "All statuses" },
                        { value: "pending", label: "Pending" },
                        { value: "generating", label: "Generating" },
                        { value: "needs_review", label: "Needs Review" },
                        { value: "saved", label: "Saved" },
                        { value: "failed", label: "Failed" },
                        { value: "skipped", label: "Skipped" },
                      ]}
                    />
                  </Col>
                </Row>
              </Card>
              <Card size="small" style={{ borderRadius: 6 }} bodyStyle={{ padding: 0 }}>
                <Table
                  rowKey="_id"
                  size="middle"
                  tableLayout="fixed"
                  columns={itemColumns}
                  dataSource={filteredCourseItems}
                  pagination={{ pageSize: 15, showSizeChanger: true, pageSizeOptions: [10, 15, 25, 50] }}
                  scroll={{ x: "max-content" }}
                  expandable={{
                    expandedRowRender: (record) => {
                      const jt = jobDetail?.job?.type;
                      if (jt === "SkillsV2") {
                        const autoLinked = record.decisions?.autoLinked || [];
                        const held = record.decisions?.held || [];
                        const missing = record.decisions?.missing || [];
                        return (
                          <div>
                            <div style={{ marginBottom: 8 }}>
                              <Text strong>Candidates: </Text>
                              <Text>{record.candidateSkills?.length || 0}</Text>
                            </div>
                            <div style={{ marginBottom: 8 }}>
                              <Text strong>Auto-linked: </Text>
                              {autoLinked.length ? autoLinked.map((entry) => <Tag key={entry.skillId}>{entry.skillName}</Tag>) : <Text type="secondary">None</Text>}
                            </div>
                            <div style={{ marginBottom: 8 }}>
                              <Text strong>Held: </Text>
                              {held.length
                                ? held.map((entry, idx) => (
                                    <Tag key={`${entry.skillId}-${idx}`} color="orange">
                                      {entry.skillName || entry.skillId} ({entry.reason})
                                    </Tag>
                                  ))
                                : <Text type="secondary">None</Text>}
                            </div>
                            <div style={{ marginTop: 12 }}>
                              <div style={{ fontWeight: 600, marginBottom: 8 }}>Missing Taxonomy Gaps</div>
                              <V2MissingSkillsReview
                                item={record}
                                jobId={selectedJobId}
                                missing={missing}
                                skillOptions={skillOptions}
                                onSaved={() => {
                                  loadSkills();
                                  loadJobs();
                                  loadJobDetail(selectedJobId);
                                }}
                              />
                            </div>
                          </div>
                        );
                      }
                      if (jt === "LevelBulk") {
                        const d = record.decisions || {};
                        const scores = d.scores || {};
                        const disagreement = d.previousLevelName && d.levelName && d.previousLevelName !== d.levelName;
                        return (
                          <Space direction="vertical" size={12} style={{ width: '100%' }}>
                            <Row gutter={[16, 8]}>
                              <Col xs={24} md={12}>
                                <div style={{ padding: 12, border: '1px solid #f0f0f0', borderRadius: 6, background: '#fafafa' }}>
                                  <div style={{ fontSize: 11, color: '#718096', marginBottom: 4 }}>Current Level</div>
                                  <div style={{ fontSize: 15, fontWeight: 600 }}>{d.previousLevelName || <Text type="secondary">Not assigned</Text>}</div>
                                </div>
                              </Col>
                              <Col xs={24} md={12}>
                                <div style={{ padding: 12, border: `1px solid ${disagreement ? '#ffa39e' : '#b7eb8f'}`, borderRadius: 6, background: disagreement ? '#fff2f0' : '#f6ffed' }}>
                                  <div style={{ fontSize: 11, color: '#718096', marginBottom: 4 }}>AI Suggests</div>
                                  <div style={{ fontSize: 15, fontWeight: 600 }}>
                                    {d.levelName || d.levelId}
                                    {d.dryRun && <Tag color="gold" style={{ marginLeft: 8 }}>preview</Tag>}
                                    {d.autoApplied === false && !d.dryRun && <Tag color="orange" style={{ marginLeft: 8 }}>low confidence</Tag>}
                                  </div>
                                </div>
                              </Col>
                            </Row>
                            {(() => {
                              const contextText = d.levelSummary || d.level_summary || d.level_context || record.llmOutput?.parsed?.level_summary || record.llmOutput?.parsed?.levelSummary || record.llmOutput?.parsed?.level_context || record.llmOutput?.parsed?.levelContext;
                              return contextText ? (
                                <div style={{ padding: 12, border: '1px solid #e2e8f0', borderRadius: 6, background: '#f8fafc' }}>
                                  <div style={{ fontSize: 11, color: '#718096', marginBottom: 4 }}>AI Level Summary (For Learner)</div>
                                  <div style={{ fontSize: 13, fontStyle: 'italic', color: '#2d3748' }}>"{contextText}"</div>
                                </div>
                              ) : null;
                            })()}
                            {Object.keys(scores).length > 0 && (
                              <div style={{ padding: 12, border: '1px solid #f0f0f0', borderRadius: 6, background: '#fafafa' }}>
                                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Confidence Scores</div>
                                {Object.entries(scores).sort((a, b) => b[1] - a[1]).map(([levelName, score]) => {
                                  const isCurrent = (d.levelName || "").toLowerCase() === levelName.toLowerCase();
                                  return (
                                    <div key={levelName} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
                                      <span style={{ width: 100, fontSize: 12, fontWeight: isCurrent ? 600 : 400, color: isCurrent ? '#1677ff' : '#4a5568', textTransform: 'capitalize', flexShrink: 0 }}>
                                        {levelName}
                                      </span>
                                      <Progress percent={Math.round(score * 100)} size="small" style={{ flex: 1, margin: 0 }} strokeColor={isCurrent ? '#1677ff' : '#d9d9d9'} showInfo={false} />
                                      <span style={{ width: 45, fontSize: 12, fontWeight: isCurrent ? 600 : 400, color: isCurrent ? '#1677ff' : '#718096', textAlign: 'right', flexShrink: 0 }}>
                                        {Math.round(score * 100)}%
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            <Collapse ghost style={{ marginTop: 8 }}>
                              <Collapse.Panel header="Raw LLM Output" key="1">
                                <pre style={{ whiteSpace: 'pre-wrap', fontSize: 11, maxHeight: 220, overflow: 'auto', margin: 0, background: '#f8fafc', padding: 8, borderRadius: 4 }}>
                                  {JSON.stringify(record.llmOutput?.parsed || record.llmOutput, null, 2)}
                                </pre>
                              </Collapse.Panel>
                            </Collapse>
                          </Space>
                        );
                      }
                      if (jt === "MetaTagsBulk") {
                        const d = record.decisions || {};
                        return (
                          <Space direction="vertical" style={{ width: "100%" }}>
                            <div><Text strong>Meta title</Text><div>{d.metaTitle}</div></div>
                            <div><Text strong>Meta description</Text><div style={{ whiteSpace: "pre-wrap" }}>{d.metaDescription}</div></div>
                            {d.dryRun ? <Tag color="gold">preview — not written to DB</Tag> : null}
                            <pre style={{ whiteSpace: "pre-wrap", fontSize: 11, maxHeight: 220, overflow: "auto", margin: 0, background: "#fff", padding: 8, borderRadius: 4 }}>
                              {JSON.stringify(record.llmOutput?.parsed || record.llmOutput, null, 2)}
                            </pre>
                          </Space>
                        );
                      }
                      if (jt === "FaqsBulk") {
                        const preview = record.decisions?.faqsPreview || [];
                        return (
                          <Space direction="vertical" style={{ width: "100%" }}>
                            {preview.map((faq, i) => (
                              <Card key={i} size="small" title={<Text strong>{faq.question}</Text>}>
                                <div style={{ whiteSpace: "pre-wrap", fontSize: 13 }}>{faq.answer}</div>
                              </Card>
                            ))}
                            {record.decisions?.replaceFaqs && <Tag>replace mode</Tag>}
                            {record.decisions?.dryRun ? <Tag color="gold">preview</Tag> : null}
                          </Space>
                        );
                      }
                      return <Text type="secondary">Expand is only populated for supported processors.</Text>;
                    },
                  }}
                />
              </Card>
            </Space>
          </Drawer>

          <AIGeneratorConfig
            visible={bulkV2ConfigVisible}
            onClose={() => {
              setBulkV2ConfigVisible(false);
              loadAiDefaultsForProcessor();
            }}
            category={aiSettingsCategoryForProcessor(pipelineType)}
            defaultPrompt={
              pipelineType === "SkillsV2"
                ? "You are an expert curriculum taxonomy classifier. Classify each candidate skill against course content using SYNONYM, HYPONYM, DISTINCT, or UNCERTAIN."
                : pipelineType === "MetaTagsBulk"
                ? META_TAGS_BULK_SYSTEM_PROMPT
                : pipelineType === "FaqsBulk"
                ? "You are an expert course instructor. Generate highly relevant Frequently Asked Questions and their detailed answers for this course."
                : "You are an expert academic classifier. Classify the course into one of the following levels: Beginner, Intermediate, Advanced, or All Levels based on the curriculum and depth."
            }
            defaultUserPrompt={
              pipelineType === "MetaTagsBulk"
                ? META_TAGS_BULK_USER_PROMPT
                : undefined
            }
            contextInstructions={
              pipelineType === "SkillsV2"
                ? "Return JSON with keys selected and missing. selected must list skillId, skillName, classification, reasoning. classification must be one of SYNONYM, HYPONYM, DISTINCT, UNCERTAIN. missing items: name, optional suggestedParent, reasoning."
                : pipelineType === "MetaTagsBulk"
                ? META_TAGS_BULK_INTERNAL_INSTRUCTIONS
                : pipelineType === "FaqsBulk"
                ? 'Return ONLY valid JSON: {"faqs": [{"question": "<question text>", "answer": "<detailed answer text>"}]}.'
                : 'Return ONLY valid JSON: {"levelId":"<24-hex Mongo ObjectId from catalog>","levelName":"<name>","scores":{"<LevelName1>":<0.0-1.0>,"<LevelName2>":<0.0-1.0>},"level_summary":"<1 sentence expectations summary>"}. levelId MUST be copied exactly from the catalog list. scores MUST contain a confidence value (0.0 to 1.0) for EVERY level in the catalog, summing to approximately 1.0. level_summary MUST set realistic expectations directly to the learner.'
            }
            excludeContextFields={pipelineType === "SkillsV2" ? ["skills"] : pipelineType === "LevelBulk" ? ["level"] : []}
          />

          <Modal
            title={<Text strong style={{ fontSize: 16 }}>{`Manual Skill Generation — ${manualCourseTitle}`}</Text>}
            open={manualModalVisible}
            onCancel={handleCloseManualModal}
            footer={null}
            width={850}
            destroyOnClose
          >
            {manualCourseId && (
              <CourseSkillsTab 
                courseId={manualCourseId} 
                onSaveSuccess={handleCloseManualModal}
              />
            )}
          </Modal>
        </div>
      </div>
    </Page>
  );
};

export default AIBulkJobs;
